import axiosInstance from './axiosInstance';

/**
 * 메시지(1:1 대화) API
 *
 * 설계 합의 사항
 *  - 대화는 '상대별 단일 스레드'다. 공고별로 나누지 않고,
 *    conversation.castingPost는 문맥 표시용(없을 수 있음)이다.
 *  - 삭제는 내 쪽에서만 사라진다(soft delete). 새 메시지가 오면 다시 나타난다.
 *  - 차단하면 대화는 읽기 전용으로 남는다(목록에서 사라지지 않는다).
 *  - '메시지 수신 허용'은 제작사 전용. 단, 이미 대화가 있으면 무시된다.
 */

/** 백엔드 미배포 구간을 화면에서 구분하기 위한 헬퍼 */
export const isMessagingUnavailable = (err) => {
  const s = err?.response?.status;
  return s === 404 || s === 405 || s === 501;
};

/** 대화 목록 — last_message_at 내림차순 */
export const getConversations = async () => {
  const res = await axiosInstance.get('/api/messages/conversations');
  const body = res.data ?? {};
  const list = Array.isArray(body) ? body : (body.conversations ?? body.content ?? []);
  return Array.isArray(list) ? list : [];
};

/**
 * 대화의 메시지 — 커서 페이징.
 * 오프셋을 쓰면 대화 중 새 메시지가 끼어들 때 경계가 밀려 중복·누락이 생긴다.
 * @param {{ before?: number, limit?: number }} opts before = 이 messageId 이전
 */
export const getMessages = async (conversationId, { before, limit = 50 } = {}) => {
  const res = await axiosInstance.get(`/api/messages/conversations/${conversationId}/messages`, {
    params: { before, limit },
  });
  const body = res.data ?? {};
  const list = Array.isArray(body) ? body : (body.messages ?? body.content ?? []);
  return {
    messages: Array.isArray(list) ? list : [],
    hasMore: Boolean(body.hasMore ?? (Array.isArray(list) && list.length === limit)),
  };
};

/** 첫 발신 — 대화가 없으면 생성하고, 있으면 그 대화에 이어 붙인다 */
export const startConversation = async ({ targetUserId, castingPostId, body }) => {
  const res = await axiosInstance.post('/api/messages/conversations', {
    targetUserId, castingPostId: castingPostId ?? undefined, body,
  });
  return res.data;
};

export const sendMessage = async (conversationId, { body, attachmentFileIds } = {}) => {
  const res = await axiosInstance.post(`/api/messages/conversations/${conversationId}/messages`, {
    body,
    attachmentFileIds: attachmentFileIds?.length ? attachmentFileIds : undefined,
  });
  return res.data;
};

export const markConversationRead = async (conversationId) => {
  await axiosInstance.patch(`/api/messages/conversations/${conversationId}/read`);
};

/** 내 목록에서만 숨김 — 상대의 기록은 남는다 */
export const deleteConversation = async (conversationId) => {
  await axiosInstance.delete(`/api/messages/conversations/${conversationId}`);
};

/** 차단/해제 — 차단해도 대화는 읽기 전용으로 남는다 */
export const setConversationBlocked = async (conversationId, blocked) => {
  await axiosInstance.patch(`/api/messages/conversations/${conversationId}/block`, { blocked });
};

/** 사이드바 뱃지용 전체 안읽음 수 */
export const getUnreadCount = async () => {
  const res = await axiosInstance.get('/api/messages/unread-count');
  const body = res.data ?? {};
  return Number(body.count ?? body.unreadCount ?? body ?? 0) || 0;
};

/**
 * 발신 가능 여부 — 보내기 버튼을 미리 막기 위한 조회.
 * 최종 판정은 서버가 하며, 이건 UI 힌트일 뿐이다.
 * @returns {{ allowed: boolean, reason?: 'RECEIVER_CLOSED'|'BLOCKED'|'I_BLOCKED' }}
 */
export const canSendMessage = async (targetUserId) => {
  const res = await axiosInstance.get('/api/messages/can-send', { params: { targetUserId } });
  return res.data ?? { allowed: true };
};

/** 제작사 '메시지 수신 허용' 설정 */
export const getMessageSettings = async () => {
  const res = await axiosInstance.get('/api/producer/message-settings');
  return res.data ?? {};
};

export const updateMessageSettings = async (allowIncomingMessages) => {
  const res = await axiosInstance.patch('/api/producer/message-settings', { allowIncomingMessages });
  return res.data;
};
